import React from 'react';
import { MapPin, Mail, Phone, MessageSquare, ArrowRight } from 'lucide-react';

interface MobileContactCardProps {
  contact: any;
  onViewDemands?: (contactId: string) => void;
  onDeleteContact?: (contactId: string, contactName: string) => void;
  onCardClick?: (contact: any) => void;
}

const TYPE_LABEL: Record<string, { label: string; color: string }> = {
  PREFEITURA:  { label: 'Prefeitura',   color: 'text-blue-600   bg-blue-50   dark:bg-blue-950/30   dark:text-blue-300'   },
  CAMARA:      { label: 'Câmara',       color: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-950/30 dark:text-indigo-300' },
  HOSPITAL:    { label: 'Hospital',     color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-300' },
  ONG:         { label: 'ONG',          color: 'text-violet-600 bg-violet-50 dark:bg-violet-950/30 dark:text-violet-300' },
  EMPRESA:     { label: 'Empresa',      color: 'text-amber-600  bg-amber-50  dark:bg-amber-950/30  dark:text-amber-300'  },
  ASSOCIACAO:  { label: 'Associação',   color: 'text-rose-600   bg-rose-50   dark:bg-rose-950/30   dark:text-rose-300'   },
  PESSOA:      { label: 'Pessoa',       color: 'text-slate-600  bg-slate-100 dark:bg-slate-800     dark:text-slate-400'  },
  OUTRO:       { label: 'Outro',        color: 'text-slate-500  bg-slate-100 dark:bg-slate-800     dark:text-slate-400'  },
};

export const MobileContactCard: React.FC<MobileContactCardProps> = ({
  contact,
  onViewDemands,
  onDeleteContact,
  onCardClick,
}) => {
  const type = TYPE_LABEL[contact.type] ?? { label: contact.type ?? 'Outro', color: 'text-slate-500 bg-slate-100' };
  const initial = (contact.nameEntity ?? 'C').charAt(0).toUpperCase();

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (contact.phone) window.location.href = `tel:${contact.phone}`;
  };

  const handleWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (contact.phone) {
      const clean = contact.phone.replace(/\D/g, '');
      window.open(`https://wa.me/${clean}`, '_blank');
    }
  };

  return (
    <article 
      onClick={() => onCardClick?.(contact)}
      className="mobile-card bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-sm cursor-pointer active:bg-slate-50 dark:active:bg-slate-800/80 transition-colors"
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-sm text-white shrink-0"
          style={{ background: 'linear-gradient(135deg, #1B2D72 0%, #1565C0 100%)' }}
        >
          {initial}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-snug truncate">
                {contact.nameEntity}
              </h3>
              {contact.responsible && (
                <p className="text-[10px] text-slate-500 truncate">{contact.responsible}</p>
              )}
            </div>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md shrink-0 ${type.color}`}>
              {type.label}
            </span>
          </div>

          {/* City */}
          {contact.city && (
            <div className="flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
              <span className="text-[10px] text-slate-500">{contact.city}{contact.uf ? ` — ${contact.uf}` : ''}</span>
            </div>
          )}
        </div>
      </div>

      {/* Contact details */}
      {(contact.phone || contact.email) && (
        <div className="mt-3 space-y-1.5">
          {contact.phone && (
            <div className="flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-xs text-slate-600 dark:text-slate-400 font-mono">{contact.phone}</span>
            </div>
          )}
          {contact.email && (
            <div className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-xs text-slate-500 truncate">{contact.email}</span>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2">
          {/* Call */}
          {contact.phone && (
            <button
              onClick={handleCall}
              className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 text-xs font-bold transition-all hover:bg-emerald-100 touch-target"
            >
              <Phone className="w-4 h-4" />
              Ligar
            </button>
          )}

          {/* WhatsApp */}
          {contact.phone && (
            <button
              onClick={handleWhatsApp}
              className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 text-xs font-bold transition-all hover:bg-green-100 touch-target"
            >
              <MessageSquare className="w-4 h-4" />
              WhatsApp
            </button>
          )}
        </div>

        {/* Ver demandas e Excluir */}
        <div className="flex items-center gap-2">
          {onViewDemands && (
            <button
              onClick={(e) => { e.stopPropagation(); onViewDemands(contact.id); }}
              className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold transition-all hover:bg-slate-200 touch-target"
            >
              Ver Demandas
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {onDeleteContact && (
            <button
              onClick={(e) => { e.stopPropagation(); onDeleteContact(contact.id, contact.nameEntity); }}
              className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-xl bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 text-xs font-bold transition-all hover:bg-red-100 touch-target"
              title="Excluir"
            >
              Excluir
            </button>
          )}
        </div>
      </div>
    </article>
  );
};
